// The RUN layer: the read + write pair behind the "Run cycle" control.
//
// This is the ignition for the whole pipeline. reporting-cycle-worker is deployed and
// research-job-runner routes job_type `run_reporting_cycle` to it, but until now nothing
// ever created that first job — golden CSVs landed in reporting_golden_rows and the cycle
// sat at snapshot forever. enqueue_reporting_cycle writes the one queue row that starts it.
//
// WHY THE READ GOES THROUGH AN RPC AND NOT A TABLE SELECT
// research_ingest_jobs' RLS read policy is can_read_research() — a RESEARCH capability,
// entirely orthogonal to reporting access. An Operations reviewer who can drive this island
// end to end may hold no research grant at all, so a direct .from("research_ingest_jobs")
// select would return zero rows and this UI would confidently report "no run in flight"
// while a run was in flight — and then offer to start a second one. get_reporting_cycle_run
// is a SECURITY DEFINER reader behind the SAME can_review_reporting() gate, so what the
// control shows and what the control may do are the same predicate.
//
// POLLING: only while a job is actually in flight, and only every 5s. A finished/failed run
// is a static fact and does not need re-fetching; an idle cycle costs nothing.
//
// NOTE (2026-08-11): enqueue_reporting_cycle / get_reporting_cycle_run are NOT YET APPLIED —
// they live in supabase/migrations/20260805T0100_enqueue_reporting_cycle.sql. The generated
// supabase types therefore don't know them, hence the `any` cast, exactly as
// useReportingAccess / useCreateReportingCycle do for the other 0003c RPCs. Drop the cast
// when types.ts is regenerated.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** The jsonb envelope get_reporting_cycle_run returns. Always an object, never null. */
export interface ReportingCycleRun {
  job_id: string | null;
  status?: string | null;
  /** True iff status is one of the runnable states (queued | running | retry). */
  in_flight: boolean;
  attempts?: number | null;
  max_attempts?: number | null;
  /** Pushed ~30 days out when a <job_type>_paused feature flag parks the job. */
  next_run_at?: string | null;
  last_error?: string | null;
  result?: Record<string, any> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const IDLE: ReportingCycleRun = { job_id: null, in_flight: false };

export function reportingCycleRunKey(cycleId: string | undefined) {
  return ["get_reporting_cycle_run", cycleId];
}

export function useReportingCycleRun(cycleId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: reportingCycleRunKey(cycleId),
    enabled: !!cycleId && enabled,
    staleTime: 5 * 1000,
    // Poll ONLY while something is actually running. react-query passes the current query;
    // read the last data off it rather than closing over state that would be stale.
    refetchInterval: (query) => ((query.state.data as ReportingCycleRun)?.in_flight ? 5000 : false),
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_reporting_cycle_run", {
        _cycle_id: cycleId,
      });
      if (error) throw error;
      return ((data ?? IDLE) as ReportingCycleRun) ?? IDLE;
    },
  });
}

/**
 * Enqueue one run of the cycle. Returns the job id — which may be an EXISTING job's id, since
 * the RPC is idempotent per cycle: if a run is already queued/running/retry it hands that one
 * back rather than starting a second tick-loop. Two loops on one cycle would double-write the
 * reconciliation ledger and could double-fire the deliver-stage newsletter send.
 */
export function useEnqueueReportingCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cycleId: string) => {
      const { data, error } = await (supabase as any).rpc("enqueue_reporting_cycle", {
        _cycle_id: cycleId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_jobId, cycleId) => {
      // The run row is the thing that just changed; the cycle row will follow as the worker
      // ticks, and the list shows the stage cursor too.
      qc.invalidateQueries({ queryKey: reportingCycleRunKey(cycleId) });
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle", cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
    },
  });
}
